import React from 'react';
import {WorkspaceRow} from '../../store/selectors/workspace.selectors';
import AudioFileRow, {AudioFileRowActions} from './AudioFileRow';
import {SearchIcon, UploadCloudIcon, XIcon} from '../Icons';

interface AudioFileTableProps extends AudioFileRowActions {
  rows: WorkspaceRow[];
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onUploadClick: () => void;
  secondsRemaining: number;
  isQuotaKnown: boolean;
  busyConversionId: string | null;
  /** The row an upload is currently going into, so it shows progress rather than a Resume button. */
  uploadingAudioId: string | null;
  uploadPercent: number;
  /** True while any upload is running — Resume is blocked on every other row while it is. */
  isUploadBusy: boolean;
}

const HEADINGS = [
  {label: 'File', span: 'md:col-span-4'},
  {label: 'Duration', span: 'md:col-span-1'},
  {label: 'Uploaded', span: 'md:col-span-2'},
  // "Status", not "Transcription": the column also carries upload state, and a file that is still
  // uploading — or that was uploaded and never transcribed — has no transcription to report.
  {label: 'Status', span: 'md:col-span-2'},
  {label: 'Conversions', span: 'md:col-span-1'},
  {label: '', span: 'md:col-span-2'},
];

const AudioFileTable: React.FC<AudioFileTableProps> = ({
  rows,
  isLoading,
  search,
  onSearchChange,
  onUploadClick,
  secondsRemaining,
  isQuotaKnown,
  busyConversionId,
  uploadingAudioId,
  uploadPercent,
  isUploadBusy,
  ...rowActions
}) => {
  const hasSearch = search.trim().length > 0;

  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-800">
        <h2 className="text-sm font-semibold text-gray-100">
          Audio files
          {rows.length > 0 && <span className="ml-2 font-normal text-gray-500">{rows.length}</span>}
        </h2>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
          <input
            type="search"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search by filename…"
            aria-label="Search audio files by filename"
            className="w-56 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
             text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
             focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {hasSearch && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>

      {rows.length > 0 && (
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 border-b border-gray-800
         bg-gray-950/40 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          {HEADINGS.map(heading => (
            <div key={heading.label || 'actions'} className={`col-span-12 ${heading.span}`}>
              {heading.label}
            </div>
          ))}
        </div>
      )}

      {isLoading && rows.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">Loading your audio files…</div>
      )}

      {!isLoading && rows.length === 0 && !hasSearch && (
        <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
          <p className="text-sm font-medium text-gray-200">No audio files yet</p>
          <p className="text-sm text-gray-500">Upload a file to get started.</p>
          <p className="max-w-md text-xs text-gray-600">
            Only transcribe what you actually need to censor — conversions are unlimited.
          </p>
          <button
            type="button"
            onClick={onUploadClick}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5
             text-xs font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            <UploadCloudIcon className="w-3.5 h-3.5" />
            Upload audio
          </button>
        </div>
      )}

      {!isLoading && rows.length === 0 && hasSearch && (
        <div className="px-4 py-12 text-center">
          <p className="text-sm text-gray-400">No files match “{search.trim()}”.</p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
          >
            Clear search
          </button>
        </div>
      )}

      {rows.map(row => (
        <AudioFileRow
          key={row.audio.id}
          row={row}
          secondsRemaining={secondsRemaining}
          isQuotaKnown={isQuotaKnown}
          busyConversionId={busyConversionId}
          isUploadingThisRow={row.audio.id === uploadingAudioId}
          uploadPercent={uploadPercent}
          isUploadBusy={isUploadBusy}
          {...rowActions}
        />
      ))}
    </section>
  );
};

export default AudioFileTable;
